import React, { useEffect } from 'react';
import Table, { CellProps } from 'rsuite/Table';
import { TableDateCell } from '../../common/table/table-date-cell.component';
import { CURRENCIES } from '../../../consts/db-values';
import { TableMoneyCallComponent } from '../../common/table/table-money-call.component';
import { UserInternalIdCell } from '../../common/table/user-internal-id-cell.component';
import Pagination from 'rsuite/Pagination';
import { usePaginationQs } from '../../../utils/usePagination';
import { SupplierCell } from '../../common/table/supplier-cell';
import {
  CleanTransaction,
  RbacObject,
} from '../../../export-types/cleaned-types';
import {
  Direction,
  TransactionService,
  useTransactionsList,
} from '../../../services/transaction.service';
import { AlertService } from '../../../services/alert.service';
import { Rbac } from '../../common/Rbac';
import { getFileV2ApiUrl } from '../../../utils/common.utils';
import PdfIcon from '../../../assets/pdf-file.svg';
import { Gallery } from '../../common/gallery/gallery';
import { Controller, useForm } from 'react-hook-form';
import { useDebouncedValue } from '@mantine/hooks';
import { InputWrapper } from '../../common/input-wrapper';
import { DateRangePicker } from 'rsuite';
import moment from 'moment';
import { RawDateValue } from '../../../utils/date-value';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';

type Form = {
  userIdInternal: string;
  dates: string[];
};

const defaultItemsPerPage = 20;

export const WriteOffsTable = () => {
  const intl = useIntl();
  const { take, skip, handleSetPage, handleChangeLimit, page } =
    usePaginationQs(defaultItemsPerPage);

  const [direction, setDirection] = React.useState<Direction>('desc');

  const { register, control, watch } = useForm<Form>({
    defaultValues: {
      userIdInternal: '',
      dates: [],
    },
  });

  const [userInternalIdDebounced] = useDebouncedValue(
    watch('userIdInternal'),
    200,
  );

  // Взять даты в диапозоне которых взять транзакции
  const [start, end] = watch(['dates'])[0];

  // Получить список транзакций с сервера
  const {
    data: transactions,
    isLoading,
    error,
    mutate,
  } = useTransactionsList({
    type: 'writeOff',
    status: ['confirmed', 'draft'],
    take,
    skip,
    direction,
    start,
    end,
    userInternalId: userInternalIdDebounced,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [take]);

  const CurrencyCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanTransaction>) => {
    return (
      rowData && (
        <Table.Cell {...props}>
          {CURRENCIES[rowData.currency as keyof typeof CURRENCIES]}
        </Table.Cell>
      )
    );
  };

  const WriteOffName = ({ rowData, ...props }: CellProps<CleanTransaction>) =>
    rowData && (
      <Table.Cell {...props}>
        {rowData.privateComment
          ? rowData.privateComment
          : rowData.comment
            ? rowData.comment
            : (rowData.purchaseOrder && (
                <FormattedMessage
                  defaultMessage="Оплата выкупа"
                  id="WriteoffsTable.title.PaymentRansom"
                  description="Пояснение к оплате"
                />
              )) ||
              (rowData.deliveryOrder && (
                <FormattedMessage
                  defaultMessage="Оплата доставки"
                  id="WriteoffsTable.title.DeliveryPayment"
                  description="Пояснение к оплате"
                />
              ))}
      </Table.Cell>
    );

  const OrderIdCell = ({ rowData, ...props }: CellProps<CleanTransaction>) =>
    rowData && (
      <Table.Cell {...props}>
        {rowData.purchaseOrder && `V${rowData.purchaseOrder.internalId}`}
        {rowData.deliveryOrder && `D${rowData.deliveryOrder.internalId}`}
      </Table.Cell>
    );

  const MoneyCell = (props: CellProps<CleanTransaction>) => {
    return (
      props.rowData && (
        <TableMoneyCallComponent {...props} currency={props.rowData.currency} />
      )
    );
  };

  const CheckedCell = ({ rowData, ...props }: CellProps<CleanTransaction>) => {
    const checkTransaction = (
      event: React.MouseEvent<HTMLDivElement>,
      transaction: CleanTransaction,
    ) => {
      if (transaction.status !== 'confirmed') {
        const result = window.confirm(
          'Вы действительно хотите подтвердить платёж?',
        );
        if (!result) {
          event.preventDefault();
          return;
        }

        TransactionService.checkTransaction(transaction.id).then(() => {
          AlertService.success();
          mutate();
        });
      }
    };

    return (
      rowData && (
        <Rbac
          object={RbacObject.Transaction}
          action={'write:admin'}
          children={
            <Table.Cell {...props} align="center">
              <div
                className="form-check text-center d-inline-block"
                onClick={(event) => checkTransaction(event, rowData)}
                title={
                  rowData.status === 'confirmed'
                    ? 'Платёж подтверждён'
                    : 'Подтвердить платёж'
                }
              >
                <input
                  className="form-check-input p-2 border-2 cursor-pointer"
                  type="checkbox"
                  disabled={rowData.status === 'confirmed'}
                  checked={rowData.status === 'confirmed'}
                  onClick={() => {}}
                />
              </div>
            </Table.Cell>
          }
          otherwise={() => (
            <Table.Cell {...props} align="center">
              <div
                className="form-check text-center d-inline-block"
                title={
                  rowData.status === 'confirmed'
                    ? 'Платёж подтверждён'
                    : 'Платёж не подтверждён'
                }
              >
                <input
                  className="form-check-input p-2 border-2 cursor-pointer"
                  type="checkbox"
                  disabled
                  defaultChecked={rowData.status === 'confirmed'}
                />
              </div>
            </Table.Cell>
          )}
        />
      )
    );
  };

  const ProofTransaction = ({
    rowData,
    ...props
  }: CellProps<CleanTransaction>) => {
    if (!rowData) return null;
    if (!rowData.proofFile) {
      return (
        <Table.Cell
          {...props}
          className="no-padding-cell pt-2"
          align="center"
        />
      );
    }

    let anchorAttributes;
    let thumbnail = getFileV2ApiUrl(rowData.proofFile.id, '48x32');
    const original = getFileV2ApiUrl(rowData.proofFile.id);

    if (rowData.proofFile.type === 'pdf') {
      thumbnail = PdfIcon;
      anchorAttributes = {
        'data-iframe': true,
        'data-src': original,
      };
    }

    return (
      <Table.Cell {...props} className="no-padding-cell pt-2" align="center">
        <Gallery>
          <a href={original} {...anchorAttributes}>
            <img
              className="rounded fit-cover"
              src={thumbnail}
              width={rowData.proofFile.type === 'pdf' ? 32 : 48}
              height={32}
            />
          </a>
        </Gallery>
      </Table.Cell>
    );
  };

  if (error) {
    return (
      <>
        <div className="pb-4">Неизвестная ошибка</div>
      </>
    );
  }

  const total = transactions?.total ?? 0;
  const items = transactions?.items ?? [];

  const rowClassNames = (rowData: CleanTransaction) => {
    return rowData?.journal === 'supplierExpense' ? 'highlighted-row' : '';
  };
  return (
    <div>
      <div className="row mb-1 ms-1 gap-2">
        <input
          className="form-control col-auto w-25"
          type="text"
          min={0}
          step={1}
          placeholder={intl.formatMessage({
            defaultMessage: 'Поиск по id пользователя',
            id: 'TransactiosHistrory.placeholder.SearchByUserID',
            description: 'Поле ввода',
          })}
          style={{ fontSize: '14px' }}
          {...register('userIdInternal')}
        />
        <Controller
          name="dates"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <InputWrapper
                error={fieldState.error?.message}
                className="w-25 p-0"
              >
                <DateRangePicker
                  className={'w-100'}
                  format={'dd.MM.yyyy'}
                  placeholder={intl.formatMessage({
                    defaultMessage: 'Выберите дату',
                    id: 'RefillTransactiosTable.title.SelectDate',
                    description: 'Пояснение к полю ввода',
                  })}
                  isoWeek={true}
                  showOneCalendar
                  ranges={[]}
                  onChange={(v) => {
                    if (v === null) field.onChange([]);
                    else {
                      field.onChange(
                        v.map(
                          (value) =>
                            moment(value).format('DD.MM.YYYY') as RawDateValue,
                        ),
                      );
                    }
                  }}
                ></DateRangePicker>
              </InputWrapper>
            );
          }}
        />
      </div>
      <Table
        data={items}
        headerHeight={57}
        autoHeight
        loading={isLoading}
        rowClassName={rowClassNames}
      >
        <Table.Column>
          <Table.HeaderCell>Tx Id</Table.HeaderCell>
          <Table.Cell dataKey="txid" />
        </Table.Column>
        <Table.Column flexGrow={1} minWidth={120}>
          <Table.HeaderCell>
            <button
              className="d-flex gap-1 w-100 h-100 bg-transparent"
              onClick={() => {
                setDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
              }}
            >
              <FormattedMessage
                defaultMessage="Дата"
                id="TransactiosHistrory.lable.date"
                description="Вкладка"
              />{' '}
              <i
                className={classNames(
                  'bi',
                  direction === 'asc' ? 'bi-sort-up' : 'bi-sort-down',
                )}
              ></i>
            </button>
          </Table.HeaderCell>
          <TableDateCell dataKey="createdAt" />
        </Table.Column>
        <Table.Column width={120} fullText>
          <Table.HeaderCell>Поставщик</Table.HeaderCell>
          <SupplierCell />
        </Table.Column>
        <Table.Column flexGrow={1} fullText minWidth={120}>
          <Table.HeaderCell>
            <FormattedMessage
              defaultMessage="ID клиента"
              id="TransactiosHistrory.lable.ID"
              description="Вкладка"
            />
          </Table.HeaderCell>
          <UserInternalIdCell />
        </Table.Column>
        <Table.Column flexGrow={1} fullText minWidth={120}>
          <Table.HeaderCell>
            <FormattedMessage
              defaultMessage="Счет cписания"
              id="WriteoffTable.lable.WriteOffAccount"
              description="Вкладка"
            />
          </Table.HeaderCell>
          <CurrencyCell />
        </Table.Column>
        <Table.Column flexGrow={1} fullText minWidth={120}>
          <Table.HeaderCell>
            <FormattedMessage
              defaultMessage="Наименование списания"
              id="WriteoffTable.lable.WriteOffName"
              description="Вкладка"
            />
          </Table.HeaderCell>
          <WriteOffName />
        </Table.Column>
        <Table.Column flexGrow={1} fullText minWidth={120}>
          <Table.HeaderCell>
            <FormattedMessage
              defaultMessage="ID заказа"
              id="WriteoffTable.lable.OrderID"
              description="Вкладка"
            />
          </Table.HeaderCell>
          <OrderIdCell />
        </Table.Column>
        <Table.Column flexGrow={1} fullText minWidth={120}>
          <Table.HeaderCell>
            <FormattedMessage
              defaultMessage="Сумма списания"
              id="WriteoffTable.lable.WriteOffAmount"
              description="Вкладка"
            />
          </Table.HeaderCell>
          <MoneyCell dataKey="amount" />
        </Table.Column>
        <Table.Column width={100} fullText>
          <Table.HeaderCell>Курс</Table.HeaderCell>
          <TableMoneyCallComponent dataKey="currencyRateInternal" />
        </Table.Column>
        <Table.Column width={150} fullText>
          <Table.HeaderCell>Сумма списания в руб</Table.HeaderCell>
          <TableMoneyCallComponent
            dataKey="userAmount"
            currencyKey="topUpCurrency"
          />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell>Платежка</Table.HeaderCell>
          <ProofTransaction />
        </Table.Column>
        <Table.Column width={50}>
          <Table.HeaderCell children="" />
          <CheckedCell />
        </Table.Column>
      </Table>

      {total > take && (
        <Pagination
          className="mt-4 pb-4"
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="md"
          layout={['pager', '-', 'limit']}
          total={total}
          limitOptions={[defaultItemsPerPage, 50, 100]}
          limit={take}
          activePage={page}
          onChangePage={handleSetPage}
          onChangeLimit={handleChangeLimit}
        />
      )}
    </div>
  );
};
